import type { ReactNode } from 'react'
import type { TelescopeNight } from '@/lib/server/telescope'
import type { NightForecast } from '@/components/telescope/night-list'

import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { buttonClass } from '@/lib/ui'
import { formatKey, parseDateKey } from '@/lib/time'
import { formatPrice } from '@/constants/tickets'
import { Media } from '@/components/generals/media'
import { FadeLink } from '@/components/generals/fade-link'
import { Reveal, RevealLines } from '@/components/generals/reveal'
import { ForecastBadge } from '@/components/telescope/night-list'
import { NightBooking } from '@/components/telescope/night-booking'

interface TelescopeSectionProps {
    nights: TelescopeNight[]
    forecasts: Record<string, NightForecast>
    cardPayments: boolean
    todayKey: string
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

/** The roof deck: guided observing nights, what is up this month, and the next night with places left. */
export const TelescopeSection = ({ nights, forecasts, cardPayments, todayKey }: TelescopeSectionProps): ReactNode => {
    const [booking, setBooking] = useState<TelescopeNight | null>(null)
    const next = nights.find(night => night.remaining > 0) ?? nights[0]
    const { month } = parseDateKey(todayKey)

    const thisMonth = useMemo(() => {
        const targets: string[] = []

        for (const night of nights.slice(0, 5)) {
            for (const target of night.targets) {
                const short = target.split(',')[0]

                if (!targets.includes(short) && targets.length < 6) targets.push(short)
            }
        }

        return targets
    }, [nights])

    return (
        <section id='telescope-nights' aria-labelledby='telescope-title' className='relative py-28 md:py-36'>
            <div className='container-page grid gap-14 lg:grid-cols-12 lg:gap-16'>
                <Reveal className='lg:col-span-6'>
                    <Media
                        src='/images/telescope-deck.jpg'
                        alt='Telescopes on the roof deck at night under a sky full of stars'
                        sizes='(min-width: 1024px) 50vw, 100vw'
                        className='aspect-[4/5] w-full'
                    >
                        <span aria-hidden className='absolute inset-x-0 bottom-0 z-[1] h-1/3 bg-gradient-to-t from-black/80 to-transparent' />
                        <span className='label-mono absolute bottom-6 left-6 z-[2] text-star-2'>
                            The roof deck, 420 m above the sea
                        </span>
                    </Media>
                </Reveal>

                <div className='flex flex-col justify-center lg:col-span-6'>
                    <p className='label-mono text-brass'>
                        Telescope nights
                    </p>
                    <RevealLines
                        id='telescope-title'
                        lines={['Nights', 'on the roof']}
                        className='display-serif mt-5 text-5xl text-star md:text-7xl'
                    />
                    <Reveal variant='wipe' className='mt-8'>
                        <p className='text-star-2'>
                            Guided observing with one of our astronomers, never more than twelve of you at the telescopes. Real telescopes, real eyepieces, and time to look properly: the Moon&apos;s mountains, Saturn&apos;s rings, galaxies millions of years away. Then we show you how to find them again from your own garden.
                        </p>
                    </Reveal>

                    {thisMonth.length > 0 && (
                        <Reveal className='mt-10'>
                            <p className='label-mono text-star-3'>
                                {`On the deck this ${MONTH_NAMES[month - 1]}`}
                            </p>
                            <ul className='mt-4 flex flex-wrap gap-2'>
                                {thisMonth.map(target => (
                                    <li key={target} className='border border-hair px-3 py-1.5 text-sm text-star-2'>
                                        {target}
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    )}

                    {next ? (
                        <Reveal className='mt-10'>
                            <article className='border border-hair bg-ink-2 p-6 md:p-8'>
                                <div className='flex items-start justify-between gap-6'>
                                    <div>
                                        <p className='label-mono text-brass'>
                                            Next night
                                        </p>
                                        <h3 className='mt-2 font-heading text-3xl text-star'>
                                            {next.title}
                                        </h3>
                                        <p className='data-mono mt-2 text-star-2'>
                                            {`${formatKey(next.nightDate, 'long')} · ${next.startsLocal} to ${next.endsLocal}`}
                                        </p>
                                    </div>
                                    <div className='shrink-0 text-right'>
                                        <p className='font-heading text-5xl font-light leading-none text-star'>
                                            {next.remaining}
                                        </p>
                                        <p className='label-mono mt-1 text-star-3'>
                                            {`place${next.remaining === 1 ? '' : 's'} left`}
                                        </p>
                                    </div>
                                </div>
                                <div className='mt-5 border-t border-hair pt-5'>
                                    <ForecastBadge forecast={forecasts[next.id]} />
                                </div>
                                <div className='mt-6 flex flex-wrap items-center gap-5'>
                                    <button
                                        type='button'
                                        onClick={() => setBooking(next)}
                                        disabled={next.remaining === 0}
                                        className={buttonClass('primary')}
                                    >
                                        {next.remaining === 0 ? 'Fully booked' : 'Reserve a place'}
                                    </button>
                                    <p className={cn('data-mono text-star-3')}>
                                        {`${formatPrice(next.pricePence)} · members ${formatPrice(next.memberPricePence)}`}
                                    </p>
                                </div>
                            </article>
                        </Reveal>
                    ) : (
                        <p className='mt-10 text-star-2'>
                            New telescope nights are being planned. Join the Clear skies list to hear first.
                        </p>
                    )}

                    <FadeLink href='/telescope-nights' className='group mt-8 inline-flex items-center gap-3 text-star'>
                        <span className='h-px w-8 bg-brass transition-all duration-500 group-hover:w-12' />
                        <span className='label-mono'>
                            Every telescope night
                        </span>
                    </FadeLink>
                </div>
            </div>

            <NightBooking night={booking} cardPayments={cardPayments} onClose={() => setBooking(null)} />
        </section>
    )
}
